<script setup>
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';
import { h, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import { isTeletelCallCenterEnabledForAccount } from 'dashboard/helper/teletelCallCenter';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import TeletelRemindersAPI from 'dashboard/api/teletel/reminders';
import { emitter } from 'shared/helpers/mitt';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { isTeletelFeatureEnabled } = useTeletelFeatures();

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isAccountFeatureEnabled = featureName =>
  isFeatureEnabledonAccount.value(accountId.value, featureName);

// The Security page only configures SAML single sign-on. It needs the account
// flag AND the installation to allow the saml login method; with only the flag
// the page renders a "disabled" notice, so the entry is hidden until both hold
// (owner order 2026-09-21). The route itself stays reachable and unchanged.
const isSamlConfigured = () =>
  isAccountFeatureEnabled(FEATURE_FLAGS.SAML) &&
  (window.chatwootConfig?.allowedLoginMethods || ['email']).includes('saml');

// Teletel: gates the Teletel audit trail entry. Independent of any Chatwoot
// feature flag, since the audit trail is Teletel-owned and always available.
const teletelCurrentRole = useMapGetter('getCurrentRole');
const currentUser = useMapGetter('getCurrentUser');
const teletelUserPermissions = computed(() =>
  getUserPermissions(currentUser.value, accountId.value)
);
const hasTeletelPermission = permission =>
  teletelCurrentRole.value === 'administrator' ||
  teletelUserPermissions.value.includes(permission);
const hasLifecycle = computed(() =>
  Boolean(isTeletelFeatureEnabled('lifecycle'))
);
const canManageLifecycle = computed(
  () => hasLifecycle.value && hasTeletelPermission('lifecycle_manage')
);
const canViewLifecycleReports = computed(
  () => hasLifecycle.value && hasTeletelPermission('report_manage')
);
const canManageSla = computed(
  () =>
    Boolean(isTeletelFeatureEnabled('sla')) &&
    hasTeletelPermission('sla_manage')
);
// Teletel AI Supervisor: administrators or roles carrying supervisor_manage,
// gated by TELETEL_SUPERVISOR_ENABLED.
const canViewSupervisor = computed(
  () =>
    Boolean(isTeletelFeatureEnabled('supervisor')) &&
    hasTeletelPermission('supervisor_manage')
);
// Teletel assistant: administrators or roles carrying assistant_manage,
// gated by TELETEL_ASSISTANT_ENABLED.
const canViewAssistant = computed(
  () =>
    Boolean(isTeletelFeatureEnabled('assistant')) &&
    hasTeletelPermission('assistant_manage')
);

const hasAdvancedAssignment = computed(() => {
  return Boolean(isTeletelFeatureEnabled('advanced_assignment'));
});

const hasTeletelCompanies = computed(() =>
  Boolean(isTeletelFeatureEnabled('companies'))
);

const hasTeletelCallCenter = computed(() => {
  return isTeletelCallCenterEnabledForAccount(accountId.value);
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
  if (!currentAccountId || !userId) return;
  store.dispatch('sidebarSortPreferences/initialize');
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const allUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);
const getLabelUnreadCount = useMapGetter(
  'conversationUnreadCounts/getLabelUnreadCount'
);
const getTeamUnreadCount = useMapGetter(
  'conversationUnreadCounts/getTeamUnreadCount'
);
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const getSidebarSectionSort = useMapGetter(
  'sidebarSortPreferences/getSectionSort'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

watch([accountId, currentUserId], fetchSidebarSortPreferences, {
  immediate: true,
});

const getSortOptionsForSection = section =>
  getSidebarSortOptions(section, {
    hasUnreadCounts: hasConversationUnreadCounts.value,
  });

const getSortForSection = section =>
  resolveSidebarSort(section, getSidebarSectionSort.value(section), {
    hasUnreadCounts: hasConversationUnreadCounts.value,
  });

const updateSortPreference = (section, sortBy) => {
  store.dispatch('sidebarSortPreferences/setSectionSort', {
    section,
    sortBy,
  });
};

const buildSortConfig = section => ({
  sortOptions: getSortOptionsForSection(section),
  activeSort: getSortForSection(section),
  onSortChange: sortBy => updateSortPreference(section, sortBy),
});

const sortedFolders = computed(() =>
  sortSidebarItems(conversationCustomViews.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
    labelKey: view => view.name,
  })
);

const sortedTeams = computed(() =>
  sortSidebarItems(teams.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
    labelKey: team => team.name,
    unreadCountKey: team => getTeamUnreadCount.value(team.id),
  })
);

const sortedInboxes = computed(() =>
  sortSidebarItems(inboxes.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.CHANNELS),
    labelKey: inbox => inbox.name,
    unreadCountKey: inbox => getInboxUnreadCount.value(inbox.id),
  })
);

const sortedLabels = computed(() =>
  sortSidebarItems(labels.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.LABELS),
    labelKey: label => label.title,
    unreadCountKey: label => getLabelUnreadCount.value(label.id),
  })
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  ...(canViewLifecycleReports.value
    ? [
        {
          name: 'Reports Customer Lifecycle',
          label: t('TELETEL_LIFECYCLE.REPORTS.SIDEBAR_LABEL'),
          to: accountScopedRoute('lifecycle_reports'),
        },
      ]
    : []),
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    // Employee efficiency is a tab on the agent overview page.
    activeOn: ['agent_reports_show', 'employee_efficiency'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

// Due follow-up reminders for the signed-in agent: paints the sidebar entry
// red the moment one falls due (cable event) and after any change.
const teletelReminderDue = ref(0);
const loadTeletelReminderDue = async () => {
  try {
    const { data } = await TeletelRemindersAPI.list({ status: 'pending' });
    teletelReminderDue.value = data.payload.filter(
      reminder => new Date(reminder.remind_at) <= new Date()
    ).length;
  } catch {
    teletelReminderDue.value = 0;
  }
};
onMounted(() => {
  loadTeletelReminderDue();
  emitter.on('teletelReminderDue', loadTeletelReminderDue);
  emitter.on('teletelReminderChanged', loadTeletelReminderDue);
});
onUnmounted(() => {
  emitter.off('teletelReminderDue', loadTeletelReminderDue);
  emitter.off('teletelReminderChanged', loadTeletelReminderDue);
});

const menuItems = computed(() => {
  return [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-teletel-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    ...(hasTeletelCallCenter.value
      ? [
          {
            name: 'Call Center',
            label: t('SIDEBAR.CALL_CENTER'),
            icon: 'i-teletel-call-center',
            to: accountScopedRoute('teletel_call_center'),
          },
        ]
      : []),
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-teletel-conversations',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-teletel-all-conversations',
          badgeCount: allUnreadCount.value,
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        ...(teletelCurrentRole.value === 'administrator'
          ? [
              {
                name: 'Unconfirmed delivery',
                label: t('TELETEL_DELIVERY_UNKNOWNS.LIST.TITLE'),
                icon: 'i-lucide-circle-alert',
                to: accountScopedRoute('teletel_delivery_unknowns'),
              },
            ]
          : []),
        {
          name: 'My reminders',
          label: t('SIDEBAR.TELETEL_REMINDERS'),
          icon: 'i-teletel-reminders',
          badgeCount: teletelReminderDue.value,
          activeOn: ['teletel_reminders_index'],
          to: accountScopedRoute('teletel_reminders_index'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          icon: 'i-teletel-mentions',
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          icon: 'i-teletel-participating',
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          icon: 'i-teletel-unattended',
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-teletel-folders',
          activeOn: ['conversations_through_folders'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.FOLDERS),
          collapsible: true,
          showTreeLine: true,
          children: sortedFolders.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-teletel-teams',
          activeOn: ['conversations_through_team'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.TEAMS),
          collapsible: true,
          showTreeLine: true,
          children: sortedTeams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            badgeCount: getTeamUnreadCount.value(team.id),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
      ],
    },
    {
      name: 'Channels',
      label: t('SIDEBAR.CHANNELS'),
      icon: 'i-teletel-channels',
      activeOn: ['conversation_through_inbox'],
      ...buildSortConfig(SIDEBAR_SORT_SECTIONS.CHANNELS),
      collapsible: true,
      showTreeLine: true,
      children: sortedInboxes.value.map(inbox => ({
        name: `${inbox.name}-${inbox.id}`,
        label: inbox.name,
        badgeCount: getInboxUnreadCount.value(inbox.id),
        icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
        to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
        component: leafProps =>
          h(ChannelLeaf, {
            label: leafProps.label,
            active: leafProps.active,
            inbox,
            badgeCount: leafProps.badgeCount,
          }),
      })),
    },
    {
      name: 'Labels',
      label: t('SIDEBAR.LABELS'),
      icon: 'i-teletel-labels',
      activeOn: ['conversations_through_label'],
      ...buildSortConfig(SIDEBAR_SORT_SECTIONS.LABELS),
      collapsible: true,
      showTreeLine: true,
      children: sortedLabels.value.map(label => ({
        name: `${label.title}-${label.id}`,
        label: label.title,
        badgeCount: getLabelUnreadCount.value(label.id),
        icon: h('span', {
          class: `size-[8px] rounded-sm`,
          style: { backgroundColor: label.color },
        }),
        to: accountScopedRoute('label_conversations', {
          label: label.title,
        }),
      })),
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-teletel-contacts',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        ...(hasLifecycle.value && hasTeletelPermission('contact_manage')
          ? [
              {
                name: 'Lifecycle board',
                label: t('TELETEL_LIFECYCLE.BOARD.SIDEBAR_LABEL'),
                to: accountScopedRoute('contacts_lifecycle_board'),
                activeOn: ['contacts_lifecycle_board'],
              },
            ]
          : []),
        {
          name: 'Segments',
          icon: 'i-teletel-segments',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          collapsible: true,
          showTreeLine: true,
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-teletel-labels',
          label: t('SIDEBAR.TAGGED_WITH'),
          collapsible: true,
          showTreeLine: true,
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-teletel-reports',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        ...(canManageSla.value
          ? [
              {
                name: 'Reports SLA',
                label: t('SIDEBAR.REPORTS_SLA'),
                to: accountScopedRoute('sla_reports'),
              },
            ]
          : []),
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-teletel-campaigns',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        ...(isAccountFeatureEnabled(FEATURE_FLAGS.WHATSAPP_CAMPAIGNS)
          ? [
              {
                name: 'WhatsApp',
                label: t('SIDEBAR.WHATSAPP'),
                to: accountScopedRoute('campaigns_whatsapp_index'),
              },
            ]
          : []),
        {
          name: 'Campaign reports',
          label: t('SIDEBAR.CAMPAIGN_REPORTS'),
          to: accountScopedRoute('campaigns_reports_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-teletel-help-center',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    ...(hasTeletelCompanies.value
      ? [
          {
            name: 'Companies',
            label: t('SIDEBAR.COMPANIES'),
            icon: 'i-teletel-companies',
            children: [
              {
                name: 'All Companies',
                label: t('SIDEBAR.ALL_COMPANIES'),
                to: accountScopedRoute(
                  'companies_dashboard_index',
                  {},
                  { page: 1, search: undefined }
                ),
                activeOn: [
                  'companies_dashboard_index',
                  'companies_dashboard_show',
                ],
              },
            ],
          },
        ]
      : []),
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-teletel-settings',
      // Owner order 2026-09-17: the settings list is grouped by function
      // instead of by the order the pages were added. Every entry keeps the
      // exact gate it had (permission, feature grant, plan flag); only the
      // position and the section heading changed. A section with nothing
      // accessible hides itself (SidebarSubGroup).
      children: [
        {
          name: 'Settings Section Account',
          label: t('SIDEBAR.SETTINGS_SECTIONS.ACCOUNT'),
          children: [
            {
              name: 'Settings Account Settings',
              label: t('SIDEBAR.ACCOUNT_SETTINGS'),
              icon: 'i-teletel-account',
              to: accountScopedRoute('general_settings_index'),
            },
            ...(teletelCurrentRole.value === 'administrator'
              ? [
                  {
                    name: 'Settings Teletel Branding',
                    teletelFeature: 'branding',
                    label: t('TELETEL_BRANDING.HEADER'),
                    icon: 'i-teletel-security',
                    to: accountScopedRoute('teletel_branding'),
                  },
                ].filter(item => isTeletelFeatureEnabled(item.teletelFeature))
              : []),
            // The owner's Messenger/Instagram connection record. The server
            // limits it to TELETEL_META_GUIDE_ACCOUNT_IDS (default: account 1);
            // the sidebar mirrors that default so other companies never see it.
            ...(teletelCurrentRole.value === 'administrator' &&
            Number(accountId.value) === 1
              ? [
                  {
                    name: 'Settings Teletel Meta Guide',
                    label: t('TELETEL_META_GUIDE.HEADER'),
                    icon: 'i-teletel-channels',
                    to: accountScopedRoute('teletel_meta_guide'),
                  },
                ]
              : []),
            // Meta status & notices (owner order 2026-09-25): every
            // administrator, every account with a WhatsApp Cloud number.
            ...(teletelCurrentRole.value === 'administrator' &&
            isTeletelFeatureEnabled('meta_status')
              ? [
                  {
                    name: 'Settings Teletel Meta',
                    label: t('TELETEL_META.HEADER'),
                    icon: 'i-teletel-channels',
                    to: accountScopedRoute('teletel_meta'),
                  },
                ]
              : []),
            ...(isSamlConfigured()
              ? [
                  {
                    name: 'Settings Security',
                    label: t('SIDEBAR.SECURITY'),
                    icon: 'i-teletel-security',
                    to: accountScopedRoute('security_settings_index'),
                  },
                ]
              : []),
            ...(isOnChatwootCloud.value
              ? [
                  {
                    name: 'Settings Billing',
                    label: t('SIDEBAR.BILLING'),
                    icon: 'i-teletel-billing',
                    to: accountScopedRoute('billing_settings_index'),
                  },
                ]
              : []),
          ],
        },
        {
          name: 'Settings Section Team',
          label: t('SIDEBAR.SETTINGS_SECTIONS.TEAM'),
          children: [
            {
              name: 'Settings Agents',
              label: t('SIDEBAR.AGENTS'),
              icon: 'i-teletel-profile',
              to: accountScopedRoute('agent_list'),
            },
            {
              name: 'Settings Teams',
              label: t('SIDEBAR.TEAMS'),
              icon: 'i-teletel-agents',
              activeOn: [
                'settings_teams_list',
                'settings_teams_new',
                'settings_teams_finish',
                'settings_teams_add_agents',
                'settings_teams_show',
                'settings_teams_edit',
                'settings_teams_edit_members',
                'settings_teams_edit_finish',
              ],
              to: accountScopedRoute('settings_teams_list'),
            },
            ...(isTeletelFeatureEnabled('team_presence') &&
            hasTeletelPermission('team_presence_view')
              ? [
                  {
                    name: 'Settings Teletel Team Presence',
                    label: t('TELETEL_TEAM_PRESENCE.HEADER'),
                    icon: 'i-lucide-radio-tower',
                    to: accountScopedRoute('teletel_team_presence'),
                  },
                ]
              : []),
            ...(teletelCurrentRole.value === 'administrator'
              ? [
                  {
                    name: 'Settings Teletel Roles',
                    teletelFeature: 'custom_roles',
                    label: t('TELETEL_ROLES.HEADER'),
                    icon: 'i-teletel-security',
                    to: accountScopedRoute('teletel_roles'),
                  },
                ].filter(item => isTeletelFeatureEnabled(item.teletelFeature))
              : []),
            ...(hasAdvancedAssignment.value
              ? [
                  {
                    name: 'Settings Agent Assignment',
                    label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                    icon: 'i-teletel-roles',
                    activeOn: [
                      'assignment_policy_index',
                      'agent_assignment_policy_index',
                      'agent_assignment_policy_create',
                      'agent_assignment_policy_edit',
                      'agent_capacity_policy_index',
                      'agent_capacity_policy_create',
                      'agent_capacity_policy_edit',
                    ],
                    to: accountScopedRoute('assignment_policy_index'),
                  },
                ]
              : []),
          ],
        },
        {
          name: 'Settings Section Conversations',
          label: t('SIDEBAR.SETTINGS_SECTIONS.CONVERSATIONS'),
          children: [
            {
              name: 'Settings Inboxes',
              label: t('SIDEBAR.INBOXES'),
              icon: 'i-teletel-inboxes-settings',
              activeOn: [
                'settings_inbox_list',
                'settings_inbox_show',
                'settings_inbox_new',
                'settings_inbox_finish',
                'settings_inboxes_page_channel',
                'settings_inboxes_add_agents',
              ],
              to: accountScopedRoute('settings_inbox_list'),
            },
            {
              name: 'Settings Labels',
              label: t('SIDEBAR.LABELS'),
              icon: 'i-teletel-labels',
              to: accountScopedRoute('labels_list'),
            },
            {
              name: 'Settings Custom Attributes',
              label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
              icon: 'i-teletel-code',
              to: accountScopedRoute('attributes_list'),
            },
            {
              name: 'Conversation Workflow',
              label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
              icon: 'i-teletel-flows',
              to: accountScopedRoute('conversation_workflow_index'),
            },
            // Required closing fields are edited under Conversation workflow
            // only (owner order 2026-09-29).
            // The SLA settings page fronts the independent Teletel backend, not
            // the Chatwoot Enterprise one (see sla.routes.js and
            // SlaBaseController), so it follows the Teletel permission model.
            // The server stays authoritative twice over: sla_manage
            // authorization and the TELETEL_SLA_ENABLED runtime switch. This
            // only avoids a dead menu link.
            ...(canManageSla.value
              ? [
                  {
                    name: 'Settings Sla',
                    label: t('SIDEBAR.SLA'),
                    icon: 'i-teletel-sla',
                    to: accountScopedRoute('sla_list'),
                  },
                ]
              : []),
            ...(canManageLifecycle.value
              ? [
                  {
                    name: 'Settings Customer Lifecycle',
                    label: t('TELETEL_LIFECYCLE.SETTINGS.SIDEBAR_LABEL'),
                    icon: 'i-teletel-flows',
                    to: accountScopedRoute('teletel_lifecycle_settings'),
                  },
                ]
              : []),
            // CSAT review notes are exported from Reports → CSAT (owner order
            // 2026-09-29); the separate settings page is gone.
          ],
        },
        {
          name: 'Settings Section Automation',
          label: t('SIDEBAR.SETTINGS_SECTIONS.AUTOMATION'),
          children: [
            // Flows have one door only: the "Automation & flows" gateway
            // (owner order 2026-09-16); the standalone list route redirects there.
            {
              name: 'Settings Automation',
              label: t('SIDEBAR.AUTOMATION'),
              icon: 'i-teletel-automation',
              to: accountScopedRoute('automation_list'),
            },
            {
              name: 'Settings Canned Responses',
              label: t('SIDEBAR.CANNED_RESPONSES'),
              icon: 'i-teletel-canned',
              to: accountScopedRoute('canned_list'),
            },
            {
              name: 'Settings Macros',
              label: t('SIDEBAR.MACROS'),
              icon: 'i-teletel-integrations',
              to: accountScopedRoute('macros_wrapper'),
            },
            {
              name: 'Settings Agent Bots',
              label: t('SIDEBAR.AGENT_BOTS_EXTERNAL'),
              icon: 'i-teletel-bots',
              to: accountScopedRoute('agent_bots'),
            },
            ...(canViewAssistant.value
              ? [
                  {
                    name: 'Settings Teletel Assistant',
                    label: t('TELETEL_ASSISTANT.SIDEBAR_LABEL'),
                    icon: 'i-teletel-bots',
                    to: accountScopedRoute('teletel_assistant'),
                  },
                  // The manager advisor rides on the assistant feature and the
                  // same permission; it only reads the account.
                  {
                    name: 'Settings Teletel Advisor',
                    label: t('TELETEL_ADVISOR.HEADER'),
                    icon: 'i-teletel-bots',
                    to: accountScopedRoute('teletel_advisor'),
                  },
                ]
              : []),
            // The supervisor is a control desk (rules, auto-actions, AI
            // access), not a report, so it sits beside the automations.
            ...(canViewSupervisor.value
              ? [
                  {
                    name: 'Teletel Supervisor',
                    label: t('TELETEL_SUPERVISOR.SIDEBAR_LABEL'),
                    icon: 'i-teletel-ai',
                    to: accountScopedRoute('teletel_supervisor'),
                  },
                ]
              : []),
          ],
        },
        {
          name: 'Settings Section Integrations',
          label: t('SIDEBAR.SETTINGS_SECTIONS.INTEGRATIONS'),
          children: [
            {
              name: 'Settings Integrations',
              label: t('SIDEBAR.INTEGRATIONS'),
              icon: 'i-teletel-apps',
              to: accountScopedRoute('settings_applications'),
              // Integration protection is a tab on the integrations page
              // (administrators only), not a separate entry (2026-10-11).
              activeOn: [
                'settings_applications',
                'teletel_integration_controls',
              ],
            },
            // Teletel audit trail: own route, own page, own API. The server
            // enforces the same rule, so this is convenience, not control.
            // The Chatwoot Enterprise "Audit Logs" entry used to sit beside it;
            // both read the same `audits` table, so the owner had it removed
            // (2026-09-19) and its old route now lands here.
            ...(hasTeletelPermission('audit_view')
              ? [
                  {
                    name: 'Settings Teletel Audit Trail',
                    teletelFeature: 'audit_trail',
                    label: t('TELETEL_AUDIT_TRAIL.HEADER'),
                    icon: 'i-teletel-audit',
                    to: accountScopedRoute('teletel_audit_trail'),
                  },
                ]
              : []),
          ],
        },
      ],
    },
  ];
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-teletel-panel flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-strong shadow-sm"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <Logo class="size-4" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
          <Button
            :icon="
              isRTL ? 'i-lucide-panel-right-close' : 'i-lucide-panel-left-close'
            "
            color="slate"
            variant="ghost"
            size="xs"
            class="!size-7 !p-0 text-n-slate-11 hover:!text-n-slate-12 hover:!bg-n-alpha-2 dark:hover:!bg-n-slate-9/30 flex-shrink-0"
            :title="isRTL ? 'طي القائمة الجانبية' : 'Collapse sidebar'"
            @click="snapToCollapsed"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align="start">
          <template #trigger="{ isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
            />
          </template>
        </ComposeConversation>
        <Button
          v-if="isEffectivelyCollapsed"
          :icon="
            isRTL ? 'i-lucide-panel-right-open' : 'i-lucide-panel-left-open'
          "
          color="slate"
          variant="ghost"
          size="sm"
          class="!size-8 !outline-n-weak !text-n-slate-11 hover:!text-n-slate-12 hover:!bg-n-alpha-2 dark:hover:!bg-n-slate-9/30"
          :title="isRTL ? 'توسيع القائمة الجانبية' : 'Expand sidebar'"
          @click="snapToExpanded"
        />
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="(item, itemIndex) in menuItems"
          :key="item.name"
          v-bind="item"
          :is-first-group="itemIndex === 0"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu :is-collapsed="isEffectivelyCollapsed" />
        <Button
          v-if="!isEffectivelyCollapsed"
          :icon="
            isRTL ? 'i-lucide-panel-right-close' : 'i-lucide-panel-left-close'
          "
          color="slate"
          variant="ghost"
          size="xs"
          class="!size-7 !p-0 text-n-slate-10 hover:!text-n-slate-12 hover:!bg-n-alpha-2 dark:hover:!bg-n-slate-9/30 flex-shrink-0"
          :title="isRTL ? 'طي القائمة الجانبية' : 'Collapse sidebar'"
          @click="snapToCollapsed"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
